import { useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { api } from '../api';
import { ServerUserOut as WorkflowUser } from '../types/apiRequests';

export function useWorkflowUsers(
  workflowName: string | undefined,
  isBeheerder: boolean,
  onMutate?: () => void,
) {
  const { getAccessTokenSilently } = useOidcContext();
  const [workflowUsers, setWorkflowUsers] = useState<WorkflowUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [togglingUser, setTogglingUser] = useState<string | null>(null);

  const loadWorkflowUsers = useCallback(async () => {
    if (!workflowName || !isBeheerder) return;
    setUsersLoading(true);
    try {
      const token = await getAccessTokenSilently();
      setWorkflowUsers(await api.getWorkflowUsers(token, workflowName));
    } finally {
      setUsersLoading(false);
      setUsersLoaded(true);
    }
  }, [workflowName, isBeheerder, getAccessTokenSilently]);

  const handleToggleUserAccess = useCallback(async (email: string, currentAccess: boolean) => {
    if (!workflowName) return;
    setTogglingUser(email);
    try {
      const token = await getAccessTokenSilently();
      if (currentAccess) {
        await api.removeWorkflowUser(token, workflowName, email);
      } else {
        await api.addWorkflowUser(token, workflowName, email);
      }
      await loadWorkflowUsers();
      onMutate?.();
    } finally {
      setTogglingUser(null);
    }
  }, [workflowName, getAccessTokenSilently, loadWorkflowUsers, onMutate]);

  return { workflowUsers, usersLoading, usersLoaded, loadWorkflowUsers, togglingUser, handleToggleUserAccess };
}
